(function() {
  'use strict';

  if (typeof WaveSurfer === 'undefined') {
    console.error('WaveSurfer.js is not loaded');
    return;
  }

  // ============================================================================
  // CONSTANTS & CONFIG
  // ============================================================================
  const STORAGE_KEY = 'graux_audio_player_state';
  const PREVIEW_MAX_SECONDS = 15;
  const PRODUCT_PREVIEW_MAX_SECONDS = 45;
  const WAVEFORM_CHECK_INTERVAL = 50;
  const RESTORE_DELAY = 500;
  const AUTOPLAY_DELAY = 200;
  const PLAYLIST_CACHE_TTL = 3600000; // 1 hour in milliseconds
  
  const shopDomain = window.Shopify?.shop || 
                     document.querySelector('meta[property="shopify:shop"]')?.content || '';
  const endpoint = 'https://musicplayer.shopifyappexperts.com/getaudioplaylist.php';

  // ============================================================================
  // DOM ELEMENTS
  // ============================================================================
  const DOM = {
    playerBar: document.getElementById('collection-audio-player'),
    currentTime: document.getElementById('player-current-time'),
    duration: document.getElementById('player-duration'),
    progressBar: document.getElementById('player-progress-bar'),
    playPauseBtn: document.getElementById('player-play-pause'),
    prevBtn: document.getElementById('player-prev'),
    nextBtn: document.getElementById('player-next'),
    volumeSlider: document.getElementById('player-volume'),
    meta: document.getElementById('player-meta'),
    link: document.getElementById('player-link'),
    hiddenWaveform: document.getElementById('hidden-waveform'),
    artwork: document.getElementById('player-artwork'),
    title: document.getElementById('player-title'),
    tagline: document.getElementById('player-tagline'),
    upNext: document.getElementById('player-up-next'),
    progressContainer: document.querySelector('.player-progress-container')
  };

  // ============================================================================
  // STATE
  // ============================================================================
  const state = {
    wavesurfer: null,
    audioUrl: null,
    trackTitle: null,
    productTitle: null,
    loopsCount: null,
    progressInterval: null,
    playIcon: null,
    playlistCache: {},
    playlist: [],
    productId: null,
    trackIndex: 0,
    cover: null,
    tagline: null,
    productUrl: null,
    trackBpm: null,
    trackKey: null,
    isRestoring: false,
    visualWaveform: null,
    isHeroContext: false,
    playbackContext: null,
    contextProductList: [],
    isDragging: false,
    dragContainer: null,
    wasPlayingBeforeDrag: false,
    genre: null,
  };

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================
  
  const getPreviewMaxDuration = () => {
    const productPreviewContexts = ['bundle-product', 'pack-product-preview', 'xmas-product-preview'];
    return productPreviewContexts.includes(state.playbackContext) 
      ? PRODUCT_PREVIEW_MAX_SECONDS 
      : PREVIEW_MAX_SECONDS;
  };

  const cleanTrackTitle = (title) => {
    if (!title) return '';
    let result = title.trim().replace(/\s+/g, ' ');
    result = result.replace(/\s*-\s*grauxmusic\.com\s*\(Royalty-Fair\)(\.mp3)?/i, '');
    result = result.replace(/\bGraux\b/gi, '');
    result = result.replace(/\(\s*demo\s*\)/gi, '');
    result = result.replace(/\b\d{2,3}\b/g, '');
    result = result.replace(/\b[A-G](?:#|b)?(?:maj|min|m|dim|aug|sus|m7|maj7|add9|7|9|11|13)?\b/gi, '');
    result = result.replace(/[Gg]enre\s*:\s*[^\s(\[\]\)]+/g, '');
    return result.replace(/\s+/g, ' ').trim();
  };

  const handleize = (str) => {
    return (str || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');
  };

  const debounce = (func, delay) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), delay);
    };
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  // ============================================================================
  // STORAGE FUNCTIONS
  // ============================================================================
  
  const storage = {
    save() {
      try {
        const data = {
          productId: state.productId,
          trackIndex: state.trackIndex,
          playlist: state.playlist,
          cover: state.cover,
          tagline: state.tagline,
          productUrl: state.productUrl,
          productTitle: state.productTitle,
          trackTitle: state.trackTitle,
          volume: DOM.volumeSlider ? parseInt(DOM.volumeSlider.value) : 50,
          isHeroAudioContext: state.isHeroContext,
          playbackContext: state.playbackContext,
          contextProductList: state.contextProductList,
          timestamp: Date.now()
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (e) {
        console.warn('Failed to save player state:', e);
      }
    },

    load() {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (!saved) return null;
        const data = JSON.parse(saved);
        // TTL: 1 hour
        if (Date.now() - data.timestamp > 3600000) {
          this.clear();
          return null;
        }
        return data;
      } catch (e) {
        console.warn('Failed to load player state:', e);
        return null;
      }
    },

    clear() {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        console.warn('Failed to clear player state:', e);
      }
    }
  };

  // ============================================================================
  // METADATA PARSING
  // ============================================================================
  
  const metadata = {
    parse(title) {
      const result = { bpm: null, key: null, genre: null };
      if (!title) return result;

      const cleaned = title.trim().replace(/\s+/g, ' ');
      const tokens = cleaned.split(' ');
      const keyPattern = /^[A-G](?:#|b)?(?:maj|min|m|dim|aug|sus|m7|maj7|add9|7|9|11|13)?$/i;

      // Parse from end to beginning
      for (let i = tokens.length - 1; i >= 0; i--) {
        const token = tokens[i];
        if (!token) continue;

        if (!result.bpm && /^\d{2,3}$/.test(token)) {
          result.bpm = token;
          continue;
        }

        if (!result.key && keyPattern.test(token)) {
          result.key = token;
        }

        if (result.bpm && result.key) break;
      }

      // Fallback patterns
      if (!result.bpm) {
        const bpmMatch = cleaned.match(/(\d{2,3})(?!.*\d)/);
        if (bpmMatch?.[1]) result.bpm = bpmMatch[1];
      }

      if (!result.key) {
        const keyMatch = cleaned.match(/([A-G](?:#|b)?(?:maj|min|m|dim|aug|sus|m7|maj7|add9|7|9|11|13)?)(?![a-zA-Z])/i);
        if (keyMatch?.[1]) result.key = keyMatch[1].trim();
      }

      // Parse genre
      const genreMatch = title.match(/[Gg]enre\s*:\s*([^\s(\[\]\)]+)/);
      if (genreMatch?.[1]) {
        let genre = genreMatch[1].trim();
        genre = genre.replace(/[)\]\}]+$/, '');
        const genreParts = genre.split(/[\s\-_]+/);
        const formattedParts = genreParts.map((part) => {
          if (!part) return '';
          return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
        });
        result.genre = formattedParts.join(' ');
      }

      return result;
    },

    formatKey(keyToken) {
      if (!keyToken) return '';
      const match = keyToken.trim().match(/^([A-Ga-g])(#{1}|b)?(?:\s*)(maj|major|min|minor|m|dim|aug|sus|m7|maj7|add9|7|9|11|13)?/i);
      if (!match) return keyToken;

      const root = match[1].toUpperCase();
      const accidental = match[2] === '#' ? '♯' : match[2] === 'b' ? '♭' : '';
      const qualityRaw = (match[3] || '').toLowerCase();
      const quality = ['m', 'min', 'minor'].includes(qualityRaw) ? 'minor' : 'major';

      return `${root}${accidental} ${quality}`.trim();
    },

    buildMarkup(bpm, keyToken) {
      const formattedKey = this.formatKey(keyToken);
      if (!bpm && !formattedKey) return '';

      const icon = '';
      const bpmPart = bpm ? `<span class="meta-bpm-value">${bpm}</span> BPM` : '';
      const keyPart = formattedKey ? `${bpm ? ' – ' : ''}<span class="meta-key-value">${formattedKey}</span>` : '';

      return `${bpmPart}${keyPart}`;
    },

    cleanTitle(title, meta) {
      if (!title) return title;
      
      let cleaned = title.trim();
      
      // Remove BPM
      if (meta?.bpm) {
        const bpmPattern = new RegExp('\\b' + meta.bpm.replace(/([.*+?^${}()|\[\]\/\\])/g, '\\$&') + '\\b', 'gi');
        cleaned = cleaned.replace(bpmPattern, '');
      }
      
      // Remove key (all variations)
      if (meta?.key) {
        const keyPattern = new RegExp(meta.key.replace(/([.*+?^${}()|\[\]\/\\])/g, '\\$&'), 'gi');
        cleaned = cleaned.replace(keyPattern, '');
        
        const formattedKey = this.formatKey(meta.key);
        if (formattedKey) {
          const formattedPattern = new RegExp(formattedKey.replace(/([.*+?^${}()|\[\]\/\\])/g, '\\$&'), 'gi');
          cleaned = cleaned.replace(formattedPattern, '');
        }
        
        [meta.key.toLowerCase(), meta.key.toUpperCase(), 
         meta.key.charAt(0).toUpperCase() + meta.key.slice(1).toLowerCase()].forEach(variation => {
          const pattern = new RegExp('\\b' + variation.replace(/([.*+?^${}()|\[\]\/\\])/g, '\\$&') + '\\b', 'gi');
          cleaned = cleaned.replace(pattern, '');
        });
      }
      
      // Remove genre
      cleaned = cleaned.replace(/[Gg]enre\s*:\s*[^\s(\[\]\)]+/gi, '');
      cleaned = cleaned.replace(/\s+/g, ' ').trim();

      cleaned = cleaned.replace(/\s*-\s*grauxmusic\.com\s*\(Royalty-Fair\)(?:\s*\.mp3)?$/i, '')
      .replace(/GRAUX\s*/gi, '')
      .replace(/\(DEMO\)/i, '')
      
      return cleaned;
    }
  };

  // ============================================================================
  // BUTTON STATE MANAGEMENT
  // ============================================================================
  
  const buttons = {
    updateIconState(button, isPlaying) {
      if (!button) return;
      
      const playIcon = button.querySelector('.icon-play, .xmas-play-icon__play');
      const pauseIcon = button.querySelector('.icon-pause, .xmas-play-icon__pause');
      
      if (isPlaying) {
        button.classList.add('is-playing', 'playing');
        if (playIcon) playIcon.style.display = 'none';
        if (pauseIcon) pauseIcon.style.display = 'block';
      } else {
        button.classList.remove('is-playing', 'playing');
        if (playIcon) playIcon.style.display = 'block';
        if (pauseIcon) pauseIcon.style.display = 'none';
      }
    },

    resetAllIcons(exceptEl) {
      document.querySelectorAll('.xmas-play-icon, .hero-play-btn, .demo-btn, .unlock-grid__play, .bundle-products__play, .bundle--play-btn, .pack--play-btn').forEach(icon => {
        if (icon !== exceptEl && !icon.closest('.demo-btn-contain')) {
          this.updateIconState(icon, false);
        }
      });
      
      document.querySelectorAll('.demo-btn').forEach(btn => {
        if (btn !== exceptEl) {
          this.updateIconState(btn, false);
        }
      });
    },

    findAllRelevant(productId, trackIndex) {
      const buttons = [];
      const ctx = state.playbackContext;

      // Hero
      if (ctx === 'hero' || state.isHeroContext) {
        const heroBtn = document.getElementById('hero-play-toggle');
        const heroAudio = document.getElementById('hero-audio');
        if (heroBtn && heroAudio?.dataset.productId === productId) {
          buttons.push({ element: heroBtn, type: 'hero' });
        }
        return buttons;
      }

      // Pack preview
      if (ctx === 'pack-product-preview') {
        document.querySelectorAll('.pack-product .pack--play-btn').forEach(btn => {
          const idx = parseInt(btn.dataset.trackIndex || '0');
          const pid = btn.dataset.productId;
          if (pid === productId && idx === trackIndex) {
            buttons.push({ element: btn, type: 'pack-product' });
          }
        });
        return buttons;
      }

      // Pack products
      if (ctx === 'pack-products') {
        document.querySelectorAll('.pack-products .bundle-products__play').forEach(btn => {
          const idx = parseInt(btn.dataset.trackIndex);
          const pid = btn.dataset.productId;
          if (pid === productId && !isNaN(idx) && idx === trackIndex) {
            buttons.push({ element: btn, type: 'pack-products' });
          }
        });
        return buttons;
      }

      // Bundle products
      if (ctx === 'bundle-products') {
        const track = state.playlist?.[state.trackIndex];
        let foundByHandle = false;

        // Try finding by genre-handle ID
        if (track?.genre) {
          const genreHandle = handleize(track.genre);
          const localIndex = track.localTrackIndex || trackIndex;
          const btn = document.querySelector(`.bundle-products .bundle-products__play[data-play-button-id="${genreHandle}-${localIndex}"]`);
          if (btn) {
            buttons.push({ element: btn, type: 'bundle-products' });
            foundByHandle = true;
          }
        }

        if (!foundByHandle) {
          document.querySelectorAll('.bundle-products .bundle-products__play').forEach(btn => {
            const idx = parseInt(btn.dataset.trackIndex);
            const pid = btn.dataset.productId;
            if (pid === productId && !isNaN(idx) && idx === trackIndex) {
              buttons.push({ element: btn, type: 'bundle-products' });
            }
          });
        }
        return buttons;
      }

      // Bundle product
      if (ctx === 'bundle-product') {
        document.querySelectorAll('.bundle--play-btn').forEach(btn => {
          const idx = parseInt(btn.dataset.trackIndex);
          const pid = btn.dataset.productId;
          if (!isNaN(idx) && idx === trackIndex && pid === productId) {
            buttons.push({ element: btn, type: 'bundle-product' });
          }
        });
        return buttons;
      }

      // Limited playlist
      if (ctx === 'limited-playlist' || productId === 'limited-playlist') {
        document.querySelectorAll('[id^="websyms-product-sounds"]').forEach(container => {
          container.querySelectorAll('.limited-track-btn').forEach(btnContainer => {
            const idx = parseInt(btnContainer.dataset.limitedIndex);
            const btn = btnContainer.querySelector('.demo-btn');
            if (btn && !isNaN(idx) && idx === trackIndex) {
              buttons.push({ element: btn, type: 'limited' });
            }
          });
        });
        return buttons;
      }

      // Unlock grid
      if (ctx === 'unlock-grid') {
        document.querySelectorAll('.unlock-grid__play').forEach(btn => {
          const pid = btn.dataset.productId;
          const idx = parseInt(btn.dataset.trackIndex);
          if (pid === productId && !isNaN(idx) && idx === trackIndex) {
            buttons.push({ element: btn, type: 'unlock-grid' });
          }
        });
        return buttons;
      }

      // Product sounds / default
      document.querySelectorAll(`[id^="websyms-product-sounds"][data-product-id="${productId}"]`).forEach(container => {
        // Unlock grid buttons
        container.querySelectorAll('.unlock-grid__play').forEach(btn => {
          const idx = parseInt(btn.dataset.trackIndex);
          if (!isNaN(idx) && idx === trackIndex) {
            buttons.push({ element: btn, type: 'unlock-grid' });
          }
        });

        // Demo buttons
        container.querySelectorAll('.demo-btn-contain').forEach(btnContainer => {
          const idx = parseInt(btnContainer.dataset.trackIndex);
          const btn = btnContainer.querySelector('.demo-btn');
          if (btn && !isNaN(idx) && idx === trackIndex) {
            buttons.push({ element: btn, type: 'demo' });
          }
        });
      });

      return buttons;
    },

    updateAll(isPlaying) {
      if (state.playIcon) {
        this.updateIconState(state.playIcon, isPlaying);
      }
      
      const relevantButtons = this.findAllRelevant(state.productId, state.trackIndex);
      relevantButtons.forEach(({ element }) => {
        this.updateIconState(element, isPlaying);
      });
      
      // Update main player button
      if (DOM.playPauseBtn) {
        const playIcon = DOM.playPauseBtn.querySelector('.icon-play');
        const pauseIcon = DOM.playPauseBtn.querySelector('.icon-pause');
        if (playIcon) playIcon.style.display = isPlaying ? 'none' : 'block';
        if (pauseIcon) pauseIcon.style.display = isPlaying ? 'block' : 'none';
      }
    }
  };

  // ============================================================================
  // WAVEFORM MANAGEMENT
  // ============================================================================
  
  const waveform = {
    find() {
      if (!state.productId || state.trackIndex < 0) return null;

      const ctx = state.playbackContext;

      // Hero
      if (ctx === 'hero' || state.isHeroContext) {
        const heroAudio = document.getElementById('hero-audio');
        if (heroAudio?.dataset.productId === state.productId) {
          return window.heroWavesurfer || null;
        }
        return null;
      }

      // Pack preview 
      if (ctx === 'pack-product-preview') {
        const packProductContainers = document.querySelectorAll('.pack-product');
        for (const ppc of packProductContainers) {
          const preview = ppc.querySelector(`[data-preview-track][data-product-id="${state.productId}"]`);
          if (preview) {
            const waveformId = `waveform-preview-${state.productId}-${state.trackIndex}`;
            if (window.visualWaveforms && window.visualWaveforms[waveformId]) {
              return window.visualWaveforms[waveformId];
            }
          }
        }
        return null;
      }

      // Pack products
      if (ctx === 'pack-products') {
        const container = this.findContainer(state.productId);
        if (container) {
          const waveformId = `waveform-${state.productId}-${state.trackIndex}`;
          if (window.visualWaveforms && window.visualWaveforms[waveformId]) {
            return window.visualWaveforms[waveformId];
          }
        }
        return null;
      }

      // Bundle products
      if (ctx === 'bundle-products') {
        const container = document.querySelector('.bundle-products');
        if (container) {
          // Use local track index if available (mapped in playlist)
          const track = state.playlist?.[state.trackIndex];
          const localIndex = track?.localTrackIndex || state.trackIndex;

          // Try genre-based ID first (if genre is available)
          if (track?.genre) {
            const genreHandle = handleize(track.genre);
            const genreId = `waveform-${genreHandle}-${localIndex}`;
            if (window.visualWaveforms && window.visualWaveforms[genreId]) {
              return window.visualWaveforms[genreId];
            }
          }

          // Fallback to product-based ID
          const waveformId = `waveform-${state.productId}-${localIndex}`;
          if (window.visualWaveforms && window.visualWaveforms[waveformId]) {
            return window.visualWaveforms[waveformId];
          }
        }
        return null;
      }

      // Bundle product
      if (ctx === 'bundle-product') {
        const container = document.querySelector('.bundle-product');
        if (container) {
          const waveformId = `waveform-${state.productId}-${state.trackIndex}`;
          if (window.visualWaveforms && window.visualWaveforms[waveformId]) {
            return window.visualWaveforms[waveformId];
          }
        }
        return null;
      }

      // Limited playlist
      if (ctx === 'limited-playlist' || state.productId === 'limited-playlist') {
        if (state.playlist[state.trackIndex]) {
          const targetProductId = state.playlist[state.trackIndex].productId;
          const container = this.findContainer(targetProductId);
          if (container) {
            const waveformId = `waveform-${targetProductId}-0`;
            if (window.visualWaveforms && window.visualWaveforms[waveformId]) {
              return window.visualWaveforms[waveformId];
            }
          }
        }
        return null;
      }

      // Unlock grid
      if (ctx === 'unlock-grid') {
        const container = this.findContainer(state.productId);
        if (container) {
          const waveformId = `waveform-${state.productId}-${state.trackIndex}`;
          if (window.visualWaveforms && window.visualWaveforms[waveformId]) {
            return window.visualWaveforms[waveformId];
          }
        }
        return null;
      }

      // Default - product sounds
      const container = this.findContainer(state.productId);
      if (!container || !window.visualWaveforms) return null;

      // Try different waveform ID patterns
      const ids = [
        `bundle-${state.productId}-${state.trackIndex}`,
        `waveform-${state.productId}-${state.trackIndex}`,
        `waveform-${state.trackIndex}`,
        `waveform-preview-${state.productId}-${state.trackIndex}`,
      ];

      for (const id of ids) {
        if (window.visualWaveforms[id]) {
          return window.visualWaveforms[id];
        }
      }

      // Try by global index
      const waveformEl = document.querySelector(`[data-global-index="${state.trackIndex}"]`);
      if (waveformEl?.id && window.visualWaveforms[waveformEl.id]) {
        return window.visualWaveforms[waveformEl.id];
      }

      return null;
    },

    findContainer(productId) {
      // Try product sounds
      let container = document.querySelector(`[id^="websyms-product-sounds"][data-product-id="${productId}"]`);
      if (container) return container;
      
      // Try bundle products
      const bundleContainers = document.querySelectorAll('.bundle-products');
      for (const bc of bundleContainers) {
        const tracks = bc.querySelector(`[data-product-id="${productId}"]`);
        if (tracks) return tracks;
      }
      
      // Try bundle product
      const bundleProductContainers = document.querySelectorAll('.bundle-product');
      for (const bpc of bundleProductContainers) {
        const preview = bpc.querySelector(`[data-preview-track][data-product-id="${productId}"]`);
        if (preview) return preview;
      }

      // Try pack product
      const packProductContainers = document.querySelectorAll('.pack-product');
      for (const ppc of packProductContainers) {
        const preview = ppc.querySelector(`[data-preview-track][data-product-id="${productId}"]`);
        if (preview) return preview;
      }

      // Try pack products
      const packProductsContainers = document.querySelectorAll('.pack-products');
      for (const ppc of packProductsContainers) {
        const preview = ppc.querySelector(`.bundle-products__waveform[data-product-id="${productId}"]`);
        if (preview) return preview;
      }
      
      return null;
    },

    resetAll(exceptWaveform) {
      if (window.heroWavesurfer && window.heroWavesurfer !== exceptWaveform) {
        try {
          window.heroWavesurfer.seekTo(0);
        } catch (e) {
          console.warn('Error resetting hero waveform:', e);
        }
      }
      
      if (window.visualWaveforms) {
        Object.values(window.visualWaveforms).forEach(ws => {
          if (ws && ws !== exceptWaveform) {
            try {
              ws.seekTo(0);
            } catch (e) {
              console.warn('Error resetting waveform:', e);
            }
          }
        });
      }
    },

    update(percent) {
      const target = state.visualWaveform || this.find();
      if (target && typeof target.seekTo === 'function') {
        try {
          const seekPercent = Math.min(Math.max(percent / 100, 0), 1);
          target.seekTo(seekPercent);
        } catch (e) {
          console.warn('Error updating visual waveform:', e);
        }
      }
    }
  };

  // ============================================================================
  // PROGRESS MANAGEMENT
  // ============================================================================
  
  const progress = {
    update(percent) {
      if (!state.wavesurfer || state.isDragging) return;
      
      const current = state.wavesurfer.getCurrentTime();
      const duration = Math.min(state.wavesurfer.getDuration(), getPreviewMaxDuration());
      const hasPercentArg = typeof percent === 'number' && !isNaN(percent);
      const clampedPercent = hasPercentArg ? Math.max(0, Math.min(percent, 100)) : null;
      const newCurrent = hasPercentArg ? (clampedPercent / 100) * duration : current;

      if (duration && duration > 0) {
        const safeCurrent = Math.min(newCurrent, duration);
        const percentValue = hasPercentArg ? clampedPercent : (safeCurrent / duration) * 100;
        
        if (DOM.progressBar) DOM.progressBar.style.width = percentValue + '%';
        if (DOM.currentTime) DOM.currentTime.textContent = formatTime(safeCurrent);
        if (DOM.duration && (!DOM.duration.textContent || DOM.duration.textContent === '0:00')) {
          DOM.duration.textContent = formatTime(duration);
        }
        
        if (hasPercentArg) {
          state.wavesurfer.setTime(newCurrent);
        }
        
        waveform.update(percentValue);
      }
    },

    startUpdates() {
      this.stopUpdates();
      state.progressInterval = setInterval(() => this.update(), 100);
    },

    stopUpdates() {
      if (state.progressInterval) {
        clearInterval(state.progressInterval);
        state.progressInterval = null;
      }
    },

    seek(progressPercent) {
      if (!state.wavesurfer || typeof progressPercent !== 'number' || isNaN(progressPercent)) return;
      
      const fullDuration = state.wavesurfer.getDuration();
      if (!fullDuration || fullDuration <= 0) return;
      
      const previewDuration = Math.min(fullDuration, getPreviewMaxDuration());
      if (!previewDuration || previewDuration <= 0) return;

      state.wavesurfer.seekTo(progressPercent * previewDuration / 100);
      this.update(progressPercent * 100);
    }
  };

  // ============================================================================
  // DRAG HANDLING
  // ============================================================================
  
  const drag = {
    wasPlayingDebounced: debounce(() => {
      state.wasPlayingBeforeDrag = state.wavesurfer?.isPlaying() || false;
    }, 100),

    start(e, container) {
      if (!state.wavesurfer) return;

      state.isDragging = true;
      state.dragContainer = container;
      state.wasPlayingBeforeDrag = false;
      
      this.wasPlayingDebounced();

      if (state.wasPlayingBeforeDrag) {
        state.wavesurfer.pause();
      }

      if (DOM.progressBar) {
        DOM.progressBar.classList.add('is-dragging');
      }

      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left + 5;
      const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));

      this.updatePosition(percent);

      document.addEventListener('mousemove', this.handleMove);
      document.addEventListener('mouseup', this.handleEnd);

      e.preventDefault();
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';
    },

    handleMove(e) {
      if (!state.isDragging || !state.dragContainer) return;

      const rect = state.dragContainer.getBoundingClientRect();
      const x = e.clientX - rect.left + 5;
      const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
      
      drag.updatePosition(percent);
    },

    handleEnd(e) {
      if (!state.isDragging || !state.dragContainer || !state.wavesurfer) return;

      const rect = state.dragContainer.getBoundingClientRect();
      const x = e.clientX - rect.left + 5;
      const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));

      const fullDuration = state.wavesurfer.getDuration();
      const previewDuration = Math.min(fullDuration, getPreviewMaxDuration());

      if (fullDuration && previewDuration && previewDuration > 0) {
        progress.seek(percent);
      }

      if (state.wasPlayingBeforeDrag && !state.wavesurfer.isPlaying()) {
        state.wavesurfer.play();
      }
      state.wasPlayingBeforeDrag = false;

      state.isDragging = false;
      state.dragContainer = null;

      document.removeEventListener('mousemove', drag.handleMove);
      document.removeEventListener('mouseup', drag.handleEnd);

      document.body.style.userSelect = '';
      document.body.style.cursor = '';

      if (DOM.progressBar) {
        DOM.progressBar.classList.remove('is-dragging');
      }
    },

    updatePosition(percent) {
      if (DOM.progressBar) {
        DOM.progressBar.style.width = percent + '%';
      }
      if (DOM.currentTime && state.wavesurfer) {
        const duration = Math.min(state.wavesurfer.getDuration(), getPreviewMaxDuration());
        const currentTime = (percent / 100) * duration;
        DOM.currentTime.textContent = formatTime(currentTime);
      }
    }
  };

  // ============================================================================
  // UI UPDATES
  // ============================================================================
  
  const ui = {
    updateNowPlaying(trackTitle) {
      const fallbackCover = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
      const coverSrc = state.cover || fallbackCover;
      
      if (DOM.artwork) DOM.artwork.src = coverSrc;

      if (DOM.title) {
        let cleanedTrack = (trackTitle || 'Track')
        // .replace(/\s*-\s*grauxmusic\.com\s*\(Royalty-Fair\)(?:\s*\.mp3)?$/i, '')
        // .replace(/GRAUX\s*/gi, '')
        // .replace(/\(DEMO\)/i, '');

        // const meta = { bpm: state.trackBpm, key: state.trackKey };
        // cleanedTrack = metadata.cleanTitle(cleanedTrack, meta);
        // cleanedTrack = cleanedTrack.replace(/\s{2,}/g, ' ').trim();
        cleanedTrack = cleanTrackTitle(cleanedTrack);

        // For pack/bundle previews, show "Pack Name (Preview)"
        let tempPreviewName = cleanedTrack.toLowerCase().trim();
        const isPreviewTrack = tempPreviewName === 'global preview' || tempPreviewName === 'preview' || tempPreviewName.endsWith('preview') || tempPreviewName.includes('preview');
        if (isPreviewTrack && state.productTitle && state.productTitle.toLowerCase() !== 'product') {
          cleanedTrack = state.productTitle;
        } else if (isPreviewTrack) {
          cleanedTrack = 'Preview';
        }

        DOM.title.textContent = cleanedTrack || 'Track';
      }

      if (DOM.tagline) {
        DOM.tagline.textContent = state.loopsCount ? state.loopsCount + ' guitars by GRAUX' : 'GRAUX';
      }

      if (DOM.meta) {
        DOM.meta.innerHTML = '';
      }

      if (DOM.link) {
        const hasUrl = !!state.productUrl;
        DOM.link.setAttribute('aria-disabled','false');
        DOM.link.href = hasUrl ? state.productUrl : '#';
      }

      this.updateUpNext();
    },

    updateUpNext() {
      if (!DOM.upNext) return;

      const nextTrack = this.getNextTrack();
      
      if (nextTrack) {
        // Parse metadata from the next track's title, not from current track
        // const nextTrackMeta = metadata.parse(nextTrack);
        // let cleanedTitle = metadata.cleanTitle(nextTrack, nextTrackMeta)
        //   .replace(/\s*-\s*grauxmusic\.com\s*\(Royalty-Fair\)(?:\s*\.mp3)?$/i, '')
        //   .replace(/GRAUX\s*/gi, '')
        //   .replace(/\(DEMO\)/i, '')
        //   .replace(/\s{2,}/g, ' ')
        //   .trim();
        let cleanedTitle = cleanTrackTitle(nextTrack);

        DOM.upNext.textContent = `Up Next: ${cleanedTitle || 'Track'}`;
        DOM.upNext.style.display = 'block';
      } else {
        DOM.upNext.style.display = 'none';
      }
    },

    getNextTrack() {
      // Don't show "Up Next" for hero context
      if (state.isHeroContext || state.playbackContext === 'hero') {
        return null;
      }

      // Check if there's a next track in current playlist
      if (state.playlist && state.playlist.length > 1) {
        const nextIndex = state.trackIndex + 1;
        
        // For pack-products and bundle-products, check if next index is within valid range (skip index 0)
        if (state.playbackContext === 'pack-products' || state.playbackContext === 'bundle-products') {
          // For bundle-products, only play first 4 tracks (indices 1-4)
          const maxIndex = state.playbackContext === 'bundle-products' 
            ? Math.min(4, state.playlist.length - 1)
            : state.playlist.length - 1;
          
          if (nextIndex <= maxIndex) {
            const track = state.playlist[nextIndex];
            return track.title || track.name || `Track ${nextIndex + 1}`;
          } else {
            // Would loop back to index 1 (first playable track)
            const track = state.playbackContext === 'bundle-products' ? state.playlist[0] : state.playlist[1];
            return track ? (track.title || track.name || 'Track 2') : null;
          }
        } else {
          if (nextIndex < state.playlist.length) {
            const track = state.playlist[nextIndex];
            return track.title || track.name || `Track ${nextIndex + 1}`;
          }
        }
      }

      // Check if there's a next product in context
      if (state.playbackContext && state.contextProductList && state.contextProductList.length > 1) {
        const currentProductIdStr = String(state.productId);
        const currentProductIndex = state.contextProductList.findIndex(
          pid => String(pid) === currentProductIdStr
        );
        
        if (currentProductIndex >= 0 && currentProductIndex < state.contextProductList.length - 1) {
          return 'Next Product';
        }
      }

      return null;
    },

    show() {
      if (DOM.playerBar) {
        DOM.playerBar.style.display = 'flex';
      }
    },

    hide() {
      if (DOM.playerBar) {
        DOM.playerBar.style.display = 'none';
      }

      if (state.wavesurfer?.isPlaying()) {
        state.wavesurfer.pause();
      }

      // Reset UI
      if (DOM.title) DOM.title.textContent = 'Nothing playing';
      if (DOM.tagline) DOM.tagline.textContent = state.loopsCount ? state.loopsCount + ' guitars by GRAUX' : 'GRAUX';
      if (DOM.upNext) DOM.upNext.style.display = 'none';
      if (DOM.playPauseBtn) {
        DOM.playPauseBtn.disabled = true;
        const playIcon = DOM.playPauseBtn.querySelector('.icon-play');
        const pauseIcon = DOM.playPauseBtn.querySelector('.icon-pause');
        if (playIcon) playIcon.style.display = 'block';
        if (pauseIcon) pauseIcon.style.display = 'none';
      }

      // Clear state
      Object.assign(state, {
        productId: null,
        playlist: [],
        trackIndex: 0,
        cover: null,
        tagline: null,
        productUrl: null,
        productTitle: null,
        loopsCount: null,
        playIcon: null,
        visualWaveform: null,
        playbackContext: null,
        contextProductList: []
      });

      storage.save();
    }
  };

  // ============================================================================
  // PLAYLIST MANAGEMENT
  // ============================================================================
  
  const playlist = {
    async load(productId, customShopDomain = null) {
      // Check if cache exists and is still valid (1 hour)
      const cached = state.playlistCache[productId];
      if (cached && cached.timestamp && (Date.now() - cached.timestamp < PLAYLIST_CACHE_TTL)) {
        return cached.data;
      }

      try {
        const targetShopDomain = customShopDomain || shopDomain;
        const body = new URLSearchParams({
          id: productId,
          shop: targetShopDomain,
          pagetype: 'products'
        });

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString()
        });

        const data = await response.json();
        
        if (data?.result === 'true' && Array.isArray(data.audio)) {
          // Cache with timestamp for 1 hour
          state.playlistCache[productId] = {
            data: data.audio,
            timestamp: Date.now()
          };
          return data.audio;
        }
        
        return [];
      } catch (err) {
        console.error('Playlist load error:', err);
        return [];
      }
    },

    clearExpiredCache() {
      const now = Date.now();
      Object.keys(state.playlistCache).forEach(productId => {
        const cached = state.playlistCache[productId];
        if (cached && cached.timestamp && (now - cached.timestamp >= PLAYLIST_CACHE_TTL)) {
          delete state.playlistCache[productId];
        }
      });
    },

    resolveNavigation() {
      if (!state.playbackContext || !state.contextProductList.length || !state.productId) {
        return { prev: null, next: null };
      }

      const normalizedCurrentId = String(state.productId);
      const normalizedList = state.contextProductList.map(pid => String(pid));
      const currentIndex = normalizedList.indexOf(normalizedCurrentId);

      if (currentIndex === -1) {
        return { prev: null, next: null };
      }

      return {
        prev: currentIndex > 0 ? state.contextProductList[currentIndex - 1] : null,
        next: currentIndex < state.contextProductList.length - 1 ? state.contextProductList[currentIndex + 1] : null
      };
    },

    updateNavButtons() {
      if (!DOM.prevBtn || !DOM.nextBtn) return;
      
      // For pack-products and bundle-products, we need at least 2 tracks (excluding preview at index 0)
      let hasMultipleTracks;
      if (state.playbackContext === 'pack-products' || state.playbackContext === 'bundle-products') {
        // For bundle-products, we show max 4 tracks (indices 1-4)
        const maxIndex = state.playbackContext === 'bundle-products' 
          ? Math.min(4, state.playlist.length - 1)
          : state.playlist.length - 1;
        const minIndex = state.playbackContext === 'bundle-products' ? 0 : 1; // Skip preview at index 0
        hasMultipleTracks = maxIndex > minIndex; // Need at least indices 1 and 2
      } else {
        hasMultipleTracks = state.playlist.length > 1;
      }
      
      const navTargets = this.resolveNavigation();

      DOM.prevBtn.disabled = !hasMultipleTracks && !navTargets.prev;
      DOM.nextBtn.disabled = !hasMultipleTracks && !navTargets.next;
      
      // Update "Up Next" indicator
      ui.updateUpNext();
    }
  };

  // ============================================================================
  // PLAYBACK CONTROL
  // ============================================================================
  
  const playback = {
    loadTrack(audioUrl, trackTitle, autoPlay = false) {
      if (!audioUrl) {
        console.warn('No audio URL provided');
        return;
      }

      // Cleanup existing
      if (state.wavesurfer) {
        try {
          state.wavesurfer.stop();
          state.wavesurfer.destroy();
        } catch (e) {
          console.warn('Error destroying wavesurfer:', e);
        }
        state.wavesurfer = null;
      }

      progress.stopUpdates();

      // Update state
      state.audioUrl = audioUrl;
      state.trackTitle = trackTitle || 'Unknown Track';
      state.visualWaveform = null;
      
      const parsedMeta = metadata.parse(state.trackTitle);
      state.trackBpm = parsedMeta.bpm;
      state.trackKey = parsedMeta.key;

      // Reset UI
      waveform.resetAll(null);
      buttons.resetAllIcons(state.playIcon);
      ui.updateNowPlaying(state.trackTitle);
      
      // Dispatch event
      document.dispatchEvent(new CustomEvent('grauxaudio:trackmeta', {
        detail: {
          productId: state.productId,
          trackTitle: state.trackTitle,
          bpm: state.trackBpm,
          key: state.trackKey
        }
      }));

      // Reset progress
      if (DOM.currentTime) DOM.currentTime.textContent = '0:00';
      if (DOM.duration) DOM.duration.textContent = '0:00';
      if (DOM.progressBar) DOM.progressBar.style.width = '0%';
      buttons.updateAll(false);

      // Create WaveSurfer
      try {
        state.wavesurfer = WaveSurfer.create({
          container: DOM.hiddenWaveform,
          waveColor: 'rgba(255, 255, 255, 0.1)',
          progressColor: '#ff5900',
          cursorColor: '#ff5900',
          barWidth: 2,
          barGap: 1,
          height: 0,
          responsive: true,
          normalize: true
        });

        state.wavesurfer.load(audioUrl);
        this.setupWaveformEvents(autoPlay);

        const volume = DOM.volumeSlider ? parseInt(DOM.volumeSlider.value) / 100 : 0.5;
        state.wavesurfer.setVolume(volume);
      } catch (e) {
        console.error('Error creating WaveSurfer:', e);
        if (DOM.title) DOM.title.textContent = 'Error initializing player';
        if (DOM.tagline) DOM.tagline.textContent = state.loopsCount ? state.loopsCount + ' guitars by GRAUX' : 'GRAUX';
      }
    },

    setupWaveformEvents(autoPlay) {
      state.wavesurfer.on('ready', () => {
        const duration = Math.min(state.wavesurfer.getDuration(), getPreviewMaxDuration());
        if (DOM.duration) DOM.duration.textContent = formatTime(duration);
        if (DOM.playPauseBtn) DOM.playPauseBtn.disabled = false;
        
        state.visualWaveform = waveform.find();
        waveform.resetAll(state.visualWaveform);
        
        if (autoPlay && !state.isRestoring) {
          setTimeout(() => {
            if (state.wavesurfer && !state.wavesurfer.isPlaying()) {
              state.wavesurfer.play();
            }
          }, AUTOPLAY_DELAY);
        }
      });

      state.wavesurfer.on('play', () => {
        progress.startUpdates();
        buttons.updateAll(true);
        storage.save();
      });

      state.wavesurfer.on('pause', () => {
        progress.stopUpdates();
        buttons.updateAll(false);
        storage.save();
      });

      state.wavesurfer.on('finish', () => {
        this.handleFinish();
      });

      state.wavesurfer.on('audioprocess', (time) => {
        if (time >= getPreviewMaxDuration()) {
          this.handlePreviewEnd();
        }
      });

      state.wavesurfer.on('error', (error) => {
        console.error('WaveSurfer error:', error);
        if (DOM.title) DOM.title.textContent = 'Error loading track';
        if (DOM.tagline) DOM.tagline.textContent = state.loopsCount ? state.loopsCount + ' guitars by GRAUX' : 'GRAUX';
        if (DOM.playPauseBtn) DOM.playPauseBtn.disabled = true;
      });
    },

    handleFinish() {
      // For hero context, don't advance to next tracks - just reset to start
      if (state.isHeroContext || state.playbackContext === 'hero') {
        this.resetToStart();
        return;
      }

      // For pack-products and bundle-products, start from index 1 (skip preview track at index 0)
      const minIndex = (state.playbackContext === 'pack-products') ? 1 : 0;
      // For bundle-products, only play first 4 tracks (indices 1-4)
      const maxIndex = state.playbackContext === 'bundle-products' 
        ? Math.min(4, state.playlist.length - 1)
        : state.playlist.length - 1;
      
      if (state.trackIndex < maxIndex) {
        state.trackIndex++;
        this.playCurrentIndex();
        storage.save();
        return;
      }
      
      const nextProductId = this.getNextProduct();
      if (nextProductId) {
        this.playProduct(nextProductId);
        return;
      }
      
      // Loop back to first playable track
      if (state.playbackContext === 'pack-products' || state.playbackContext === 'bundle-products') {
        state.trackIndex = minIndex;
        this.playCurrentIndex();
        storage.save();
      } else {
        this.resetToStart();
      }
    },

    handlePreviewEnd() {
      state.wavesurfer.pause();

      // For hero context, don't advance to next tracks - just reset to start
      if (state.isHeroContext || state.playbackContext === 'hero') {
        this.resetToStart();
        return;
      }

      // For pack-products and bundle-products, start from index 1 (skip preview track at index 0)
      const minIndex = (state.playbackContext === 'pack-products') ? 1 : 0;
      // For bundle-products, only play first 4 tracks (indices 1-4)
      const maxIndex = state.playbackContext === 'bundle-products' 
        ? Math.min(4, state.playlist.length - 1)
        : state.playlist.length - 1;

      if (state.trackIndex < maxIndex) {
        state.trackIndex++;
        this.playCurrentIndex();
        storage.save();
        return;
      }
      
      const nextProductId = this.getNextProduct();
      if (nextProductId) {
        this.playProduct(nextProductId);
        return;
      }
      
      // Loop back to first playable track
      if (state.playbackContext === 'pack-products' || state.playbackContext === 'bundle-products') {
        state.trackIndex = minIndex;
        this.playCurrentIndex();
        storage.save();
      } else {
        this.resetToStart();
      }
    },

    getNextProduct() {
      if (!['unlock-grid', 'limited-playlist', 'pack-products', 'bundle-products', 'bundle-product'].includes(state.playbackContext) || 
          !state.contextProductList.length) {
        return null;
      }

      const currentProductIdStr = String(state.productId);
      const currentProductIndex = state.contextProductList.findIndex(
        pid => String(pid) === currentProductIdStr
      );
      
      if (currentProductIndex >= 0 && currentProductIndex < state.contextProductList.length - 1) {
        return state.contextProductList[currentProductIndex + 1];
      }
      
      return null;
    },

    resetToStart() {
      if (DOM.currentTime) DOM.currentTime.textContent = '0:00';
      if (DOM.progressBar) DOM.progressBar.style.width = '0%';
      
      progress.stopUpdates();
      
      if (state.wavesurfer) {
        state.wavesurfer.seekTo(0);
      }
      
      buttons.updateAll(false);
      
      if (state.visualWaveform) {
        try {
          state.visualWaveform.seekTo(0);
        } catch (e) {}
      }
    },

    playCurrentIndex() {
      if (!state.playlist.length) return;
      
      const track = state.playlist[state.trackIndex];
      if (!track) return;

      const title = track.title || track.name || `Track ${state.trackIndex + 1}`;

      if (track.cover && track.cover !== state.cover) {
        state.cover = track.cover;
        ui.updateNowPlaying(title);
      }
      
      // Update playIcon to the correct button for current track
      const relevantButtons = buttons.findAllRelevant(state.productId, state.trackIndex);
      if (relevantButtons.length > 0) {
        // Find the button that matches the current context
        let targetButton = null;
        if (state.isHeroContext || state.playbackContext === 'hero') {
          targetButton = relevantButtons.find(btn => btn.type === 'hero')?.element;
        } else {
          // For other contexts, prefer demo buttons, then others
          targetButton = relevantButtons.find(btn => btn.type === 'demo')?.element ||
                        relevantButtons.find(btn => btn.type === 'unlock-grid')?.element ||
                        relevantButtons.find(btn => btn.type === 'pack-products')?.element ||
                        relevantButtons.find(btn => btn.type === 'bundle-products')?.element ||
                        relevantButtons.find(btn => btn.type === 'bundle-product')?.element ||
                        relevantButtons[0]?.element;
        }
        if (targetButton) {
          state.playIcon = targetButton;
        }
      }

      state.visualWaveform = waveform.find();
      this.loadTrack(track.file || track.url, title, true);
      playlist.updateNavButtons();
    },

    async playProduct(productId) {
      if (!productId) {
        console.warn('No product ID provided');
        return;
      }

      let targetElement = null;
      if (state.playbackContext === 'unlock-grid') {
        targetElement = document.querySelector(`.unlock-grid__play[data-product-id="${productId}"]`);
      } else if (state.playbackContext === 'limited-playlist') {
        targetElement = document.querySelector(`[id^="websyms-product-sounds"][data-product-id="${productId}"]`);
      } else if (state.playbackContext === 'pack-products') {
        targetElement = document.querySelector(`.pack-products .bundle-products__play[data-product-id="${productId}"]`);
      } else if (state.playbackContext === 'bundle-products') {
        targetElement = document.querySelector(`.bundle-products .bundle-products__play[data-product-id="${productId}"]`);
      }

      if (!targetElement) {
        console.warn('Element not found for product:', productId);
        return;
      }

      if (state.playbackContext === 'unlock-grid' || state.playbackContext === 'pack-products' || state.playbackContext === 'bundle-products') {
        state.playIcon = targetElement;
      }

      const productTitle = targetElement.dataset.productTitle || 'Product';
      const productUrl = targetElement.dataset.productUrl || null;
      const cover = targetElement.dataset.cover || null;
      const tagline = targetElement.dataset.tagline || 'GRAUX';
      const loopsCount = targetElement.dataset.loopsCount || null;
      state.loopsCount = loopsCount;

      ui.show();

      if (DOM.title) DOM.title.textContent = 'Loading...';
      if (DOM.tagline) DOM.tagline.textContent = 'Preparing preview';

      state.productId = productId;
      state.cover = cover;
      state.tagline = tagline;
      state.productUrl = productUrl;
      state.productTitle = productTitle;
      state.isHeroContext = false;

      try {
        const list = await playlist.load(productId);
        
        if (!list.length) {
          if (DOM.title) DOM.title.textContent = 'Tracks unavailable';
          if (DOM.tagline) DOM.tagline.textContent = state.loopsCount ? state.loopsCount + ' guitars by GRAUX' : 'GRAUX';
          return;
        }
        
        state.playlist = list;
        state.trackIndex = 0;
        state.visualWaveform = waveform.find();
        playlist.updateNavButtons();
        this.playCurrentIndex();
        storage.save();
      } catch (err) {
        console.error('Playlist load error', err);
        if (DOM.title) DOM.title.textContent = 'Tracks unavailable';
        if (DOM.tagline) DOM.tagline.textContent = state.loopsCount ? state.loopsCount + ' guitars by GRAUX' : 'GRAUX';
      }
    },

    async handleRequest(productId, productTitle, cover, tagline, iconEl, productUrl, prevPlaybackContext) {
      const isHeroClick = iconEl?.id === 'hero-play-toggle';
      
      // Same product - toggle or reset to first track
      if (state.productId === productId && state.wavesurfer && prevPlaybackContext === state.playbackContext) {
        if (isHeroClick && (state.trackIndex !== 0 || !state.isHeroContext)) {
          state.trackIndex = 0;
          state.isHeroContext = true;
          state.playbackContext = 'hero';
          state.contextProductList = [];
          state.playIcon = iconEl;
          if (state.playlist.length > 0) {
            const track = state.playlist[0];
            const title = track.title || track.name || 'Track 1';
            state.visualWaveform = waveform.find();
            this.loadTrack(track.file || track.url, title, true);
            playlist.updateNavButtons();
            storage.save();
          }
          return;
        }
        
        // Toggle play/pause
        if (state.wavesurfer.isPlaying()) {
          state.wavesurfer.pause();
        } else {
          state.wavesurfer.play();
        }
        return;
      }

      // New product
      ui.show();

      if (DOM.title) DOM.title.textContent = 'Loading...';
      if (DOM.tagline) DOM.tagline.textContent = 'Preparing preview';
      
      state.productTitle = productTitle;
      state.productId = productId;
      state.cover = cover;
      state.tagline = tagline;
      state.productUrl = productUrl;
      state.playIcon = iconEl;
      
      // Determine context
      if (iconEl?.id === 'hero-play-toggle') {
        state.isHeroContext = true;
        state.playbackContext = 'hero';
        state.contextProductList = [];
      } else if (iconEl?.closest('#websyms-product-sounds') && !iconEl?.closest('#hero-audio')) {
        state.isHeroContext = false;
        // Don't change playbackContext here - let handleDemoPlay set it
      } else if (!iconEl || !iconEl.closest('#hero-audio')) {
        state.isHeroContext = false;
      }

      try {
        const list = await playlist.load(productId);
        
        if (!list.length) {
          if (DOM.title) DOM.title.textContent = 'Tracks unavailable';
          if (DOM.tagline) DOM.tagline.textContent = state.loopsCount ? state.loopsCount + ' guitars by GRAUX' : 'GRAUX';
          return;
        }
        
        // For hero and xmas-product-preview context, only play the first track
        if (iconEl?.id === 'hero-play-toggle') {
          state.playlist = [list[0]];
          state.trackIndex = 0;
          state.isHeroContext = true;
        } else if (state.playbackContext === 'xmas-product-preview') {
          state.playlist = [list[0]];
          state.trackIndex = 0;
          state.isHeroContext = false;
        } else {
          state.playlist = list;
          state.trackIndex = 0;
          state.isHeroContext = false;
        }

        state.visualWaveform = waveform.find();
        playlist.updateNavButtons();
        this.playCurrentIndex();
        storage.save();
      } catch (err) {
        console.error('Playlist load error', err);
        if (DOM.title) DOM.title.textContent = 'Tracks unavailable';
        if (DOM.tagline) DOM.tagline.textContent = state.loopsCount ? state.loopsCount + ' guitars by GRAUX' : 'GRAUX';
      }
    },

    playSingle(audioUrl, productId, productTitle, cover, tagline, iconEl, productUrl) {
      if (!audioUrl) {
        console.warn('No audio URL provided for direct play');
        return;
      }

      ui.show();

      state.playlist = [{
        file: audioUrl,
        url: audioUrl,
        title: productTitle || 'Track',
        name: productTitle || 'Track'
      }];
      state.productId = productId || ('direct-' + btoa(audioUrl).replace(/=+$/, ''));
      state.trackIndex = 0;
      state.cover = cover;
      state.tagline = tagline || productTitle || 'Preview';
      state.productTitle = productTitle || 'Track';
      state.productUrl = productUrl;
      state.playIcon = iconEl;
      state.isHeroContext = false;

      playlist.updateNavButtons();
      this.loadTrack(audioUrl, productTitle || 'Track', true);
      storage.save();
    }
  };

  // ============================================================================
  // RESTORE STATE
  // ============================================================================
  
  const restore = {
    async execute() {
      const savedState = storage.load();
      if (!savedState || !savedState.productId || !savedState.playlist?.length) {
        return;
      }

      state.isRestoring = true;
      
      Object.assign(state, {
        productId: savedState.productId,
        playlist: savedState.playlist,
        trackIndex: savedState.trackIndex || 0,
        cover: savedState.cover,
        tagline: savedState.tagline,
        productUrl: savedState.productUrl,
        productTitle: savedState.productTitle,
        trackTitle: savedState.trackTitle,
        isHeroContext: savedState.isHeroAudioContext || false,
        playbackContext: savedState.playbackContext,
        contextProductList: savedState.contextProductList || []
      });

      if (DOM.volumeSlider && savedState.volume) {
        DOM.volumeSlider.value = savedState.volume;
        DOM.volumeSlider.style.setProperty('--volume-percent', savedState.volume + '%');
      }

      ui.show();
      ui.updateNowPlaying(state.trackTitle || 'Track');
      playlist.updateNavButtons();

      try {
        await playlist.load(state.productId);
        
        if (state.playlist.length > 0) {
          const track = state.playlist[state.trackIndex];
          if (track) {
            const title = track.title || track.name || `Track ${state.trackIndex + 1}`;
            playback.loadTrack(track.file || track.url, title, false);
          }
        }
        
        setTimeout(() => {
          state.isRestoring = false;
        }, RESTORE_DELAY);
      } catch (err) {
        console.error('Error restoring state:', err);
        state.isRestoring = false;
      }
    }
  };

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  
  const handlers = {
    setupPlayerControls() {
      // Play/Pause button
      if (DOM.playPauseBtn) {
        DOM.playPauseBtn.addEventListener('click', () => {
          if (!state.wavesurfer) {
            if (DOM.title) DOM.title.textContent = 'Nothing playing';
            if (DOM.tagline) DOM.tagline.textContent = state.loopsCount ? state.loopsCount + ' guitars by GRAUX' : 'GRAUX';
            return;
          }

          if (state.wavesurfer.isPlaying()) {
            state.wavesurfer.pause();
          } else {
            state.wavesurfer.play();
          }
        });
        DOM.playPauseBtn.disabled = true;
      }

      // Previous button
      if (DOM.prevBtn) {
        DOM.prevBtn.addEventListener('click', () => {
          if (state.playlist.length > 1) {
            // For pack-products and bundle-products context, skip index 0 (preview track)
            if (state.playbackContext === 'pack-products' || state.playbackContext === 'bundle-products') {
              const minIndex = state.playbackContext === 'bundle-products' ? 0 : 1; // Skip index 0
              // For bundle-products, only play first 4 tracks (indices 1-4)
              const maxIndex = state.playbackContext === 'bundle-products' 
                ? Math.min(4, state.playlist.length - 1)
                : state.playlist.length - 1;
              
              if (state.trackIndex > minIndex) {
                state.trackIndex--;
              } else {
                // Go to last track
                state.trackIndex = maxIndex;
              }
            } else {
              state.trackIndex = (state.trackIndex - 1 + state.playlist.length) % state.playlist.length;
            }
            playback.playCurrentIndex();
            storage.save();
            return;
          }

          const navTargets = playlist.resolveNavigation();
          if (navTargets.prev) {
            playback.playProduct(navTargets.prev);
          }
        });
      }

      // Next button
      if (DOM.nextBtn) {
        DOM.nextBtn.addEventListener('click', () => {
          if (state.playlist.length > 1) {
            // For pack-products and bundle-products context, skip index 0 (preview track)
            if (state.playbackContext === 'pack-products' || state.playbackContext === 'bundle-products') {
              const minIndex = state.playbackContext === 'bundle-products' ? 0 : 1; // Skip index 0
              // For bundle-products, only play first 4 tracks (indices 1-4)
              const maxIndex = state.playbackContext === 'bundle-products' 
                ? Math.min(4, state.playlist.length - 1)
                : state.playlist.length - 1;
              
              if (state.trackIndex < maxIndex) {
                state.trackIndex++;
              } else {
                // Loop back to first playable track (index 1)
                state.trackIndex = minIndex;
              }
            } else {
              state.trackIndex = (state.trackIndex + 1) % state.playlist.length;
            }
            playback.playCurrentIndex();
            storage.save();
            return;
          }

          const navTargets = playlist.resolveNavigation();
          if (navTargets.next) {
            playback.playProduct(navTargets.next);
          }
        });
      }

      // Volume slider
      if (DOM.volumeSlider) {
        DOM.volumeSlider.addEventListener('input', (e) => {
          const volume = parseInt(e.target.value) / 100;
          if (state.wavesurfer) {
            state.wavesurfer.setVolume(volume);
          }
          e.target.style.setProperty('--volume-percent', e.target.value + '%');
          storage.save();
        });
        DOM.volumeSlider.style.setProperty('--volume-percent', DOM.volumeSlider.value + '%');
      }

      // Progress bar click/drag
      if (DOM.progressContainer) {
        DOM.progressContainer.addEventListener('click', (e) => {
          if (!state.wavesurfer) return;
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const percent = Math.max(0, Math.min(1, x / rect.width));
          const fullDuration = state.wavesurfer.getDuration();
          const previewDuration = Math.min(fullDuration, getPreviewMaxDuration());
          if (fullDuration && previewDuration) {
            progress.seek(percent);
          }
        });

        DOM.progressContainer.addEventListener('mousedown', (e) => {
          drag.start(e, e.currentTarget);
        });
      }
    },

    setupTaglineClick() {
      if (DOM.tagline) {
        DOM.tagline.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          window.location.href = '/pages/biography';
        });
      }
    },

    setupPlayButtons() {
      document.addEventListener('click', async (e) => {
        // Xmas icon
        const xmasIcon = e.target.closest('.xmas-play-icon');
        if (xmasIcon) {
          e.preventDefault();
          e.stopPropagation();
          const productId = xmasIcon.dataset.productId;
          const productTitle = xmasIcon.dataset.productTitle || 'Track';
          const hasAudio = xmasIcon.dataset.hasAudio === 'true';
          const cover = xmasIcon.dataset.cover || null;
          const tagline = xmasIcon.dataset.tagline || null;
          const productUrl = xmasIcon.dataset.productUrl || null;
          const loopsCount = xmasIcon.dataset.loopsCount || null;
          if (hasAudio && productId) {
            state.loopsCount = loopsCount;
            state.isHeroContext = false;
            let prevPlaybackContext = state.playbackContext;
            state.playbackContext = 'xmas-product-preview';
            state.contextProductList = [];
            await playback.handleRequest(productId, productTitle, cover, tagline, xmasIcon, productUrl, prevPlaybackContext);
          }
          return;
        }

        // Hero button
        const heroBtn = e.target.closest('#hero-play-toggle');
        if (heroBtn) {
          e.preventDefault();
          e.stopPropagation();
          const heroAudio = document.getElementById('hero-audio');
          if (heroAudio) {
            const productId = heroAudio.dataset.productId;
            const productUrl = heroAudio.dataset.productUrl || null;
            const productTitle = document.querySelector('.product-single__title')?.textContent || 'Product';
            const coverImg = document.querySelector('.hero-cover img');
            const cover = coverImg ? coverImg.src : null;
            if (productId) {
              state.isHeroContext = true;
              let prevPlaybackContext = state.playbackContext;
              state.playbackContext = 'hero';
              state.contextProductList = [];
              await playback.handleRequest(productId, productTitle, cover, 'GRAUX', heroBtn, productUrl, prevPlaybackContext);
            }
          }
          return;
        }

        // Unlock grid play button
        const unlockGridBtn = e.target.closest('.unlock-grid__play');
        if (unlockGridBtn) {
          e.preventDefault();
          e.stopPropagation();
          await this.handleUnlockGridPlay(unlockGridBtn);
          return;
        }

        // Demo button - only if NOT inside hero-audio
        const demoBtn = e.target.closest('.demo-btn-contain');
        if (demoBtn && demoBtn.dataset.trackIndex !== undefined && !demoBtn.classList.contains('limited-track-btn')) {
          // If demo button is inside hero-audio, treat it as hero context
          const isInHeroAudio = demoBtn.closest('#hero-audio');
          if (isInHeroAudio) {
            e.preventDefault();
            e.stopPropagation();
            const heroAudio = document.getElementById('hero-audio');
            if (heroAudio) {
              const productId = heroAudio.dataset.productId;
              const productUrl = heroAudio.dataset.productUrl || null;
              const productTitle = document.querySelector('.product-single__title')?.textContent || 'Product';
              const coverImg = document.querySelector('.hero-cover img');
              const cover = coverImg ? coverImg.src : null;
              if (productId) {
                state.isHeroContext = true;
                let prevPlaybackContext = state.playbackContext;
                state.playbackContext = 'hero';
                state.contextProductList = [];
                await playback.handleRequest(productId, productTitle, cover, 'GRAUX', demoBtn, productUrl, prevPlaybackContext);
              }
            }
            return;
          } else {
            // Normal demo button outside hero-audio
            e.preventDefault();
            e.stopPropagation();
            await this.handleDemoPlay(demoBtn);
            return;
          }
        }

        // Bundle product play button
        const bundleProductBtn = e.target.closest('.bundle--play-btn');
        if (bundleProductBtn) {
          e.preventDefault();
          e.stopPropagation();
          await this.handleBundleProductPlay(bundleProductBtn);
          return;
        }

        // Bundle preview play button
        const bundlePreviewBtn = e.target.closest('.bundle--preview .bundle--play-btn');
        if (bundlePreviewBtn) {
          e.preventDefault();
          e.stopPropagation();
          await this.handleBundleProductPlay(bundlePreviewBtn);
          return;
        }

        // Pack product preview play button (check first)
        const packProductBtn = e.target.closest('.pack-product .pack--play-btn');
        if (packProductBtn) {
          e.preventDefault();
          e.stopPropagation();
          await this.handlePackProductPlay(packProductBtn);
          return;
        }

        // Pack products play button (check first to avoid conflict with bundle-products)
        const packProductsBtn = e.target.closest('.pack-products .bundle-products__play');
        if (packProductsBtn) {
          e.preventDefault();
          e.stopPropagation();
          await this.handlePackProductsPlay(packProductsBtn);
          return;
        }

        // Bundle products play button
        const bundleProductsBtn = e.target.closest('.bundle-products__play');
        if (bundleProductsBtn) {
          e.preventDefault();
          e.stopPropagation();
          await this.handleBundleProductsPlay(bundleProductsBtn);
          return;
        }

        // Close button
        const closeBtn = e.target.closest('#player-close');
        if (closeBtn) {
          e.preventDefault();
          ui.hide();
        }
      });
    },

    async handleUnlockGridPlay(btn) {
      const productId = btn.dataset.productId;
      const productUrl = btn.dataset.productUrl || null;
      const productTitle = btn.dataset.productTitle || 'Product';
      const tagline = btn.dataset.tagline || 'GRAUX';
      const trackIndex = parseInt(btn.dataset.trackIndex) || 0;
      const cover = btn.dataset.cover || null;

      if (!productId) return;

      // Setup context
      const unlockGridSection = btn.closest('.unlock-grid');
      if (unlockGridSection && state.playbackContext !== 'unlock-grid') {
        state.contextProductList = [];
        unlockGridSection.querySelectorAll('.unlock-grid__play').forEach(b => {
          const pid = b.dataset.productId;
          if (pid && !state.contextProductList.includes(pid)) {
            state.contextProductList.push(pid);
          }
        });
        state.playbackContext = 'unlock-grid';
      }

      state.isHeroContext = false;

      // Toggle if same track
      if (state.productId === productId && state.trackIndex === trackIndex && state.wavesurfer) {
        state.wavesurfer.isPlaying() ? state.wavesurfer.pause() : state.wavesurfer.play();
        return;
      }

      // Load and play
      const list = await playlist.load(productId);
      if (list?.[trackIndex]) {
        const track = list[trackIndex];
        const title = track.title || track.name || `Track ${trackIndex + 1}`;

        if (state.productId !== productId) {
          state.productId = productId;
          state.playlist = list;
          state.trackIndex = trackIndex;
          state.cover = cover;
          state.tagline = tagline;
          ui.show();
          playlist.updateNavButtons();
          storage.save();
        } else {
          state.playlist = list;
          state.trackIndex = trackIndex;
          ui.show();
          playlist.updateNavButtons();
          storage.save();
        }

        state.playIcon = btn;
        state.visualWaveform = null;

        const checkWaveform = () => {
          const waveformId = `waveform-${trackIndex}`;
          if (window.visualWaveforms?.[waveformId]) {
            state.visualWaveform = window.visualWaveforms[waveformId];
          } else {
            setTimeout(checkWaveform, WAVEFORM_CHECK_INTERVAL);
          }
        };
        checkWaveform();

        playback.loadTrack(track.file || track.url, title, true);
        state.productUrl = productUrl;
      }
    },

    async handleDemoPlay(btnContainer) {
      const trackIndex = parseInt(btnContainer.dataset.trackIndex);
      const container = btnContainer.closest('[id^="websyms-product-sounds"]');
      let prevPlaybackContext = state.playbackContext;
      if (!container || isNaN(trackIndex) || trackIndex < 0) return;

      state.isHeroContext = false;
      const productId = btnContainer.dataset.productId || container.dataset.productId;
      const productUrl = container.dataset.productUrl || null;

      // Setup context
      const limitedSection = container.closest('.limited-artist__melodies');
      if (limitedSection && state.playbackContext !== 'limited-playlist') {
        state.contextProductList = [];
        limitedSection.querySelectorAll('[id^="websyms-product-sounds"]').forEach(c => {
          const pid = c.dataset.productId;
          if (pid && !state.contextProductList.includes(pid)) {
            state.contextProductList.push(pid);
          }
        });
        state.playbackContext = 'limited-playlist';
      } else {
        state.playbackContext = 'product-sounds';
        state.contextProductList = [];
      }

      if (!productId) return;

      // Toggle if same track
      if (state.productId === productId && state.trackIndex === trackIndex && state.wavesurfer && prevPlaybackContext === state.playbackContext) {
        state.wavesurfer.isPlaying() ? state.wavesurfer.pause() : state.wavesurfer.play();
        return;
      }

      // Load playlist
      const bundlePlaylist = window.bundlePlaylists?.[productId];
      const list = bundlePlaylist || await playlist.load(productId);

      if (list?.[trackIndex]) {
        const track = list[trackIndex];
        const title = track.title || track.name || `Track ${trackIndex + 1}`;

        let cover = null;
        if (bundlePlaylist && track.cover) {
          cover = track.cover;
        } else {
          const coverImg = document.querySelector('.hero-cover img');
          cover = coverImg?.src || null;
        }

        if (state.productId !== productId) {
          state.productId = productId;
          state.playlist = list;
          state.trackIndex = trackIndex;
          state.cover = cover;
          state.tagline = 'GRAUX';
          ui.show();
          playlist.updateNavButtons();
          storage.save();
        } else {
          state.trackIndex = trackIndex;
          state.playlist = list;
          if (cover && cover !== state.cover) {
            state.cover = cover;
            ui.updateNowPlaying(title);
          }
          ui.show();
          playlist.updateNavButtons();
          storage.save();
        }

        const icon = btnContainer.querySelector('.demo-btn');
        if (icon) state.playIcon = icon;

        state.visualWaveform = null;

        const isBundleTrack = !!btnContainer.dataset.specificProductId;
        const specificProductId = btnContainer.dataset.specificProductId;
        const localIndex = btnContainer.dataset.localIndex;

        const checkWaveform = () => {
          const ids = [
            isBundleTrack ? `bundle-${productId}-${trackIndex}` : null,
            isBundleTrack && specificProductId ? `waveform-${specificProductId}-${localIndex}` : null,
            `waveform-${productId}-${trackIndex}`,
            `waveform-${trackIndex}`
          ].filter(Boolean);

          for (const id of ids) {
            if (window.visualWaveforms?.[id]) {
              state.visualWaveform = window.visualWaveforms[id];
              return;
            }
          }
          setTimeout(checkWaveform, WAVEFORM_CHECK_INTERVAL);
        };
        checkWaveform();

        playback.loadTrack(track.file || track.url, title, true);
        state.productUrl = productUrl;
      }
    },

    async handleBundleProductPlay(btn) {
      const productId = btn.dataset.productId;
      const productUrl = btn.dataset.productUrl || null;
      const productTitle = btn.dataset.productTitle || 'Product';
      const tagline = btn.dataset.tagline || 'GRAUX';
      const trackIndex = parseInt(btn.dataset.trackIndex) || 0;
      let cover = btn.dataset.cover;

      if (!cover) {
        const productImg = btn.closest('.bundle-product')?.querySelector('.bundle--image');
        cover = productImg?.src || null;
      }

      if (!productId) return;

      // Setup context
      let container = btn.closest('.bundle-product');
      if (!container) {
        // Try to find bundle-product through data-preview-track
        const previewTrack = document.querySelector(`[data-preview-track][data-product-id="${productId}"]`);
        if (previewTrack) {
          container = previewTrack.closest('.bundle-product');
        }
      }

      if (container && state.playbackContext !== 'bundle-product') {
        state.contextProductList = [];
        container.querySelectorAll('.bundle--play-btn').forEach(b => {
          const pid = b.dataset.productId;
          if (pid && !state.contextProductList.includes(pid)) {
            state.contextProductList.push(pid);
          }
        });
        state.playbackContext = 'bundle-product';
      }

      state.isHeroContext = false;

      // Toggle if same track
      if (state.productId === productId && state.trackIndex === trackIndex && state.wavesurfer) {
        state.wavesurfer.isPlaying() ? state.wavesurfer.pause() : state.wavesurfer.play();
        return;
      }

      const list = await playlist.load(productId);
      if (list?.[trackIndex]) {
        const track = list[trackIndex];
        const title = track.title || track.name || `Track ${trackIndex + 1}`;

        if (state.productId !== productId) {
          state.productId = productId;
          state.playlist = list;
          state.trackIndex = trackIndex;
          state.cover = cover;
          state.tagline = tagline;
          ui.show();
          playlist.updateNavButtons();
          storage.save();
        } else {
          state.playlist = list;
          state.trackIndex = trackIndex;
          ui.show();
          playlist.updateNavButtons();
          storage.save();
        }

        state.playIcon = btn;
        state.visualWaveform = null;

        const checkWaveform = () => {
          const ids = [`waveform-${productId}-${trackIndex}`, `waveform-${trackIndex}`];
          for (const id of ids) {
            if (window.visualWaveforms?.[id]) {
              state.visualWaveform = window.visualWaveforms[id];
              return;
            }
          }
          setTimeout(checkWaveform, WAVEFORM_CHECK_INTERVAL);
        };
        checkWaveform();

        playback.loadTrack(track.file || track.url, title, true);
        state.productUrl = productUrl;
      }
    },

    async handlePackProductsPlay(btn) {
      const productId = btn.dataset.productId;
      const productUrl = btn.dataset.productUrl || null;
      const tagline = btn.dataset.tagline || 'GRAUX';
      const trackIndex = parseInt(btn.dataset.trackIndex) || 0;
      let prevPlaybackContext = state.playbackContext;
      let cover = btn.dataset.cover;

      if (!cover) {
        const packCoverImg = btn.closest('.pack-products')?.querySelector('.pack--image');
        cover = packCoverImg?.src || null;
      }

      if (!productId) return;

      // Setup context
      const container = btn.closest('.pack-products');
      if (container && state.playbackContext !== 'pack-products') {
        state.contextProductList = [];
        container.querySelectorAll('.bundle-products__play').forEach(b => {
          const pid = b.dataset.productId;
          if (pid && !state.contextProductList.includes(pid)) {
            state.contextProductList.push(pid);
          }
        });
        state.playbackContext = 'pack-products';
      }

      state.isHeroContext = false;

      // Toggle if same track
      if (state.productId === productId && state.trackIndex === trackIndex && state.wavesurfer && prevPlaybackContext === 'pack-products') {
        state.wavesurfer.isPlaying() ? state.wavesurfer.pause() : state.wavesurfer.play();
        return;
      }

      const list = await playlist.load(productId);
      if (list?.[trackIndex]) {
        const track = list[trackIndex];
        const title = track.title || track.name || `Track ${trackIndex + 1}`;

        if (state.productId !== productId) {
          state.productId = productId;
          state.playlist = list;
          state.trackIndex = trackIndex;
          state.cover = cover;
          state.tagline = tagline;
          ui.show();
          playlist.updateNavButtons();
          storage.save();
        } else {
          state.trackIndex = trackIndex;
          state.playlist = list;
          ui.show();
          playlist.updateNavButtons();
          storage.save();
        }

        state.playIcon = btn;
        state.visualWaveform = null;

        const checkWaveform = () => {
          // For pack-products, don't search for preview waveform
          const ids = [`waveform-${productId}-${trackIndex}`, `waveform-${trackIndex}`];
          for (const id of ids) {
            if (window.visualWaveforms?.[id]) {
              state.visualWaveform = window.visualWaveforms[id];
              return;
            }
          }
          setTimeout(checkWaveform, WAVEFORM_CHECK_INTERVAL);
        };
        checkWaveform();

        playback.loadTrack(track.file || track.url, title, true);
        state.productUrl = productUrl;
      }
    },

    async handlePackProductPlay(btn) {
      const productId = btn.dataset.productId;
      const productUrl = btn.dataset.productUrl || null;
      const tagline = btn.dataset.tagline || 'GRAUX';
      const trackIndex = parseInt(btn.dataset.trackIndex) || 0;
      let cover = btn.dataset.cover;
      let prevPlaybackContext = state.playbackContext;

      if (!cover) {
        const packCoverImg = btn.closest('.pack-product')?.querySelector('.pack--image');
        cover = packCoverImg?.src || null;
      }

      if (!productId) return;

      state.isHeroContext = false;
      state.playbackContext = 'pack-product-preview';
      state.contextProductList = [];

      if (state.productId === productId &&
          state.playbackContext === 'pack-product-preview' &&
          state.trackIndex === trackIndex &&
          state.wavesurfer &&
          prevPlaybackContext === 'pack-product-preview') {
        state.wavesurfer.isPlaying() ? state.wavesurfer.pause() : state.wavesurfer.play();
        return;
      }

      const list = await playlist.load(productId);
      if (list?.[trackIndex]) {
        const track = list[trackIndex];
        const title = track.title || track.name || `Track ${trackIndex + 1}`;

        const previewPlaylist = [track];

        state.productId = productId;
        state.playlist = previewPlaylist;
        state.trackIndex = 0;
        state.cover = cover;
        state.tagline = tagline;
        state.playIcon = btn;
        state.visualWaveform = waveform.find();
        ui.show();
        playlist.updateNavButtons();
        storage.save();

        playback.loadTrack(track.file || track.url, title, true);
        state.productUrl = productUrl;
      }
    },

    async handleBundleProductsPlay(btn) {
      const productId = btn.dataset.productId;
      const productUrl = btn.dataset.productUrl || null;
      const productTitle = btn.dataset.productTitle || 'Product';
      const tagline = btn.dataset.tagline || 'GRAUX';
      const trackIndex = parseInt(btn.dataset.trackIndex) || 0;
      const genre = btn.dataset.genre;
      let cover = btn.dataset.cover;

      if (!cover) {
        const heroCoverImg = btn.closest('.bundle-products__item')?.querySelector('.bundle-products__image img');
        cover = heroCoverImg?.src || null;
      }

      if (!productId) return;

      // Setup context
      const container = btn.closest('.bundle-products');
      if (container && state.playbackContext !== 'bundle-products') {
        state.contextProductList = [];
        container.querySelectorAll('.bundle-products__play').forEach(b => {
          const pid = b.dataset.productId;
          if (pid && !state.contextProductList.includes(pid)) {
            state.contextProductList.push(pid);
          }
        });
        state.playbackContext = 'bundle-products';
      }

      state.isHeroContext = false;

      // Toggle if same track
      if (state.productId === productId && state.trackIndex === trackIndex && state.genre === genre && state.wavesurfer) {
        state.wavesurfer.isPlaying() ? state.wavesurfer.pause() : state.wavesurfer.play();
        return;
      }

      // Use unified playlist filtered by GENRE
      if (window.bundleProductsAllTracks && window.bundleProductsAllTracks.length > 0) {

        // Filter by genre
        let genrePlaylist = [];
        if (genre) {
          genrePlaylist = window.bundleProductsAllTracks.filter(p =>
            p.metadata && p.metadata.genre === genre
          );
        }

        if (genrePlaylist.length > 0) {
          // Find index of clicked track within the scoped playlist
          const playlistIndex = genrePlaylist.findIndex(p =>
            String(p.productId) === String(productId) &&
            String(p.trackIndex) === String(trackIndex)
          );

          if (playlistIndex !== -1) {
            const entry = genrePlaylist[playlistIndex];

            // Map scoped playlist to state.playlist format
            state.playlist = genrePlaylist.map(item => ({
              file: item.track.file || item.track.url,
              url: item.track.file || item.track.url,
              title: item.track.title || item.track.name, // Use raw title to preserve metadata
              name: item.track.title || item.track.name,
              productId: item.productId,
              localTrackIndex: item.trackIndex, // Keep local index (1-4)
              genre: genre, // Pass genre for waveform finding
              cover: item.track.cover || cover
            }));

            state.productId = entry.productId;
            state.trackIndex = playlistIndex;
            state.cover = cover;
            state.tagline = tagline;
            state.productTitle = productTitle;
            state.productUrl = productUrl;
            state.playIcon = btn;

            state.genre = genre;

            ui.show();

            const checkWaveform = () => {
              // Try genre-based ID first
              const genreHandle = handleize(genre);
              const ids = [
                `waveform-${genreHandle}-${trackIndex}`,
                `waveform-${productId}-${trackIndex}`,
                `waveform-${trackIndex}`
              ];
              for (const id of ids) {
                if (window.visualWaveforms?.[id]) {
                  state.visualWaveform = window.visualWaveforms[id];
                  return;
                }
              }
              // Try explicit find if ids fail
              state.visualWaveform = waveform.find();
            };
            checkWaveform();

            console.log('[Global Player] Loaded Genre Playlist:', {
              genre: genre,
              totalTracks: state.playlist.length,
              currentIndex: state.trackIndex,
              currentTrack: entry.cleanTitle
            });

            playback.loadTrack(entry.track.file || entry.track.url, entry.track.title, true);
            playlist.updateNavButtons();
            storage.save();
            return;
          }
        }
      }

      // Fallback to old per-product logic
      const list = await playlist.load(productId);
      if (list?.[trackIndex]) {
        const track = list[trackIndex];
        const title = track.title || track.name || `Track ${trackIndex + 1}`;

        state.productId = productId;
        state.playlist = list;
        state.trackIndex = trackIndex;
        state.cover = cover;
        state.tagline = tagline;
        state.productTitle = productTitle;
        state.productUrl = productUrl;
        state.playIcon = btn;

        ui.show();
        state.visualWaveform = waveform.find();
        playlist.updateNavButtons();
        storage.save();

        playback.loadTrack(track.file || track.url, title, true);
      }
    },

    setupCheckboxes() {
      document.addEventListener('change', (e) => {
        if (e.target.classList.contains('js-unlock-checkbox')) {
          const selectText = e.target.parentElement.querySelector('.unlock-grid__select-text');
          if (selectText) {
            if (e.target.checked) {
              selectText.textContent = 'selected';
              selectText.classList.add('is-selected');
            } else {
              selectText.textContent = 'select';
              selectText.classList.remove('is-selected');
            }
          }
        }
      });
    }
  };

  // ============================================================================
  // UTILITY - Hide Empty Buttons
  // ============================================================================
  
  async function checkAndHideEmptyXmasButtons() {
    const xmasIcons = document.querySelectorAll('.xmas-play-icon');
    if (!xmasIcons.length) return;

    const checks = Array.from(xmasIcons).map(async (icon) => {
      const productId = icon.dataset.productId;
      const hasAudio = icon.dataset.hasAudio === 'true';

      if (!hasAudio || !productId) {
        icon.style.display = 'none';
        return;
      }

      try {
        const list = await playlist.load(productId);
        if (!list || list.length === 0) {
          icon.style.display = 'none';
        }
      } catch (err) {
        console.warn('Error checking playlist for xmas icon:', err);
        icon.style.display = 'none';
      }
    });

    await Promise.all(checks);
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================
  
  function init() {
    if (!DOM.playerBar || !DOM.hiddenWaveform) {
      console.warn('Global audio player elements not found');
      return;
    }

    // Clear expired playlist cache on init
    playlist.clearExpiredCache();
    
    // Periodically clear expired cache (every 10 minutes)
    setInterval(() => {
      playlist.clearExpiredCache();
    }, 600000);

    handlers.setupPlayerControls();
    handlers.setupTaglineClick();
    handlers.setupPlayButtons();
    handlers.setupCheckboxes();
    checkAndHideEmptyXmasButtons();
    restore.execute();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ============================================================================
  // UI SYNC UTILITIES
  // ============================================================================
  
  const uiSync = {
    getIsGlobalPlaying() {
      const pauseIcon = document.querySelector('.player-play-pause .icon-pause');
      return pauseIcon ? pauseIcon.style.display !== 'none' : false;
    },

    getGlobalProgressPercent(lastPercent = 0) {
      const progressEl = document.querySelector('.player-progress-bar') || document.getElementById('player-progress-bar');
      if (!progressEl) return lastPercent;

      const inlineWidth = progressEl.style.width;
      if (inlineWidth) {
        const parsed = parseFloat(inlineWidth.replace('%', ''));
        if (!isNaN(parsed)) return Math.max(0, Math.min(parsed, 100));
      }

      const computedWidth = parseFloat(getComputedStyle(progressEl).width);
      const parentWidth = parseFloat(getComputedStyle(progressEl.parentElement).width);

      if (parentWidth > 0 && !isNaN(computedWidth)) {
        return Math.max(0, Math.min((computedWidth / parentWidth) * 100, 100));
      }

      return lastPercent;
    },

    setButtonState(btn, isPlaying) {
      if (!btn) return;
      btn.classList.toggle('is-playing', isPlaying);
    }
  };

  // ============================================================================
  // WAVEFORM UTILITIES
  // ============================================================================
  
  const waveformUtils = {
    async initVisualWaveform(productId, trackIndex, waveformEl, audioUrl, options = {}) {
      if (!waveformEl || typeof WaveSurfer === 'undefined') return null;

      const {
        height = 40,
        waveColor = '#888888',
        progressColor = '#ff5900',
        cursorColor = '#ff5900',
        barWidth = 2,
        barGap = 2,
        onInteraction = null,
        context = null
      } = options;

      try {
        const ws = WaveSurfer.create({
          container: waveformEl,
          height: height,
          responsive: true,
          normalize: true,
          interact: true,
          waveColor: waveColor,
          progressColor: progressColor,
          cursorColor: cursorColor,
          barWidth: barWidth,
          barGap: barGap,
        });

        if (onInteraction && typeof onInteraction === 'function') {
          const handleInteractionDebounced = debounce((newTime) => {
            onInteraction(newTime, ws);
          }, 100);
          ws.on('interaction', handleInteractionDebounced);
        }

        if (audioUrl) {
          await ws.load(audioUrl);
        }

        // Register waveform globally for player sync
        if (!window.visualWaveforms) window.visualWaveforms = {};
        const suffix = context === 'pack-product-preview' ? '-preview' : '';
        const waveformId = `waveform-${productId}-${trackIndex}${suffix}`;
        window.visualWaveforms[waveformId] = ws;
        waveformEl.id = waveformId;

        return ws;
      } catch (error) {
        console.warn('Error initializing visual waveform:', error);
        return null;
      }
    },

    updateWaveformProgress(waveformEl, ws, percent) {
      if (!waveformEl || !ws) return;

      const safePercent = Math.max(0, Math.min(percent, 100)) / 100;
      waveformEl.setAttribute('aria-valuenow', String(Math.round(safePercent * 100)));

      if (typeof ws.seekTo === 'function') {
        try {
          ws.seekTo(safePercent);
        } catch (error) {
          console.warn('Error updating waveform progress:', error);
        }
      }
    }
  };

  // ============================================================================
  // GLOBAL API
  // ============================================================================
  
  window.GrauxAudioPlayer = {
    handlePlayRequest: playback.handleRequest.bind(playback),
    loadPlaylist: playlist.load.bind(playlist),
    playSingleTrack: playback.playSingle.bind(playback),
    playCurrentIndex: playback.playCurrentIndex.bind(playback),
    playNextProduct: playback.playProduct.bind(playback),
    updatePrevNextState: playlist.updateNavButtons.bind(playlist),
    saveState: storage.save.bind(storage),
    hidePlayer: ui.hide.bind(ui),
    checkAndHideEmptyXmasButtons,
    seekToProgress: progress.seek.bind(progress),
    // Metadata utilities
    parseTrackMeta: metadata.parse.bind(metadata),
    cleanTrackTitle: metadata.cleanTitle.bind(metadata),
    formatKeyNotation: metadata.formatKey.bind(metadata),
    buildMetaMarkup: function() { return ''; },
    // UI sync utilities
    getIsGlobalPlaying: uiSync.getIsGlobalPlaying.bind(uiSync),
    getGlobalProgressPercent: uiSync.getGlobalProgressPercent.bind(uiSync),
    setButtonState: uiSync.setButtonState.bind(uiSync),
    // Waveform utilities
    initVisualWaveform: waveformUtils.initVisualWaveform.bind(waveformUtils),
    updateWaveformProgress: waveformUtils.updateWaveformProgress.bind(waveformUtils),
    // State getters/setters
    get currentTrackIndex() { return state.trackIndex; },
    set currentTrackIndex(val) { state.trackIndex = val; },
    get currentProductId() { return state.productId; },
    set currentProductId(val) { state.productId = val; },
    get currentPlaylist() { return state.playlist; },
    set currentPlaylist(val) { state.playlist = val; },
    get currentCover() { return state.cover; },
    set currentCover(val) { state.cover = val; },
    get currentTagline() { return state.tagline; },
    set currentTagline(val) { state.tagline = val; },
    get currentProductUrl() { return state.productUrl; },
    set currentProductUrl(val) { state.productUrl = val; },
    get currentProductTitle() { return state.productTitle; },
    set currentProductTitle(val) { state.productTitle = val; },
    get isHeroAudioContext() { return state.isHeroContext; },
    set isHeroAudioContext(val) { state.isHeroContext = val; },
    get currentPlayIcon() { return state.playIcon; },
    set currentPlayIcon(val) { state.playIcon = val; },
    get playbackContext() { return state.playbackContext; },
    set playbackContext(val) { state.playbackContext = val; },
    get contextProductList() { return state.contextProductList; },
    set contextProductList(val) { state.contextProductList = val; },
    get wavesurfer() { return state.wavesurfer; }
  };
})();
